import { useEffect, useMemo, useState } from 'react'
import AdminShell from './AdminShell'
import { tournamentApi } from '../../services/api'

/**
 * Super-admin: CTQ hotel-authorization worklist. Custom trips whose hotel NET was
 * over the threshold capture the customer money at completion but DON'T book the
 * hotel — the super-admin books it here once funds have settled and the corporate
 * (Expedia) card has room. "Check price" re-quotes live (quoted vs current NET);
 * "Authorize & book" books on the live provider. A price increase since the quote
 * must be explicitly confirmed before it books.
 */
const money = (n, cur = 'CAD') =>
  n == null ? '—' : new Intl.NumberFormat('en-CA', { style: 'currency', currency: cur }).format(Number(n))
const r2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100
const REASON_TEXT = {
  net_rate_manual: 'net-rate (manual) — confirm by hand',
  not_found_in_search: 'not returned by the area search',
  not_found: 'hotel id not resolved by any provider',
  no_rates: 'no rates for these dates (far-out?)',
  no_matching_rate: 'rates exist but none match the quoted room / refundability / price',
  search_error: 'search error — retry',
  no_external_id_or_center: 'missing hotel id or trip center',
  unavailable: 'unavailable',
}
const reasonText = (r) => (r ? (REASON_TEXT[r] || r) : null)
const yn = (v) => (v == null ? '—' : v ? 'yes' : 'no')
const errMsg = (e) => (e && typeof e === 'object') ? (e.message || JSON.stringify(e)) : e
function fmtDay(v) {
  if (!v) return '—'
  const ymd = String(v).slice(0, 10)
  const d = new Date(ymd + 'T00:00:00Z')
  return isNaN(d) ? ymd : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
}

export default function HotelAuthorizations() {
  const [bookings, setBookings] = useState(null)
  const [err, setErr] = useState(null)
  const [open, setOpen] = useState({})       // bookingId -> expanded
  const [check, setCheck] = useState({})      // bookingId -> { loading, hotels, anyPriceUp, anyUnbookable }
  const [confirmUp, setConfirmUp] = useState({}) // bookingId -> bool (admin confirmed new price)
  const [busy, setBusy] = useState(null)      // bookingId being authorized/checked
  const [done, setDone] = useState({})        // bookingId -> result message
  const [diag, setDiag] = useState({})        // bookingId -> { loading, hotels, error }
  const [upgrade, setUpgrade] = useState({})  // bookingId -> { [sequence]: true } (upgrade-to-refundable toggles)

  const toggleUpgrade = (bookingId, seq) =>
    setUpgrade(u => {
      const cur = { ...(u[bookingId] || {}) }
      if (cur[seq]) delete cur[seq]; else cur[seq] = true
      return { ...u, [bookingId]: cur }
    })
  // The parked-hotel sequences the admin ticked for a refundable upgrade (only those
  // whose live re-quote actually surfaced a bookable refundable alternative).
  const selectedUpgrades = (row) => {
    const chk = check[row.bookingId]
    const picks = upgrade[row.bookingId] || {}
    const list = (chk && chk.hotels) || []
    return list.filter(h => picks[h.sequence] && h.refundableAlt && h.upgradeDelta != null).map(h => h.sequence)
  }
  const selectedDeltaSum = (row) => {
    const chk = check[row.bookingId]
    const picks = upgrade[row.bookingId] || {}
    const list = (chk && chk.hotels) || []
    return r2(list.filter(h => picks[h.sequence] && h.upgradeDelta != null).reduce((s, h) => s + Number(h.upgradeDelta), 0))
  }

  function load() {
    setErr(null)
    tournamentApi.get('/admin/hotel-authorizations')
      .then(r => setBookings(r.data?.bookings || []))
      .catch(e => setErr(e?.response?.data?.message || e.message))
  }
  useEffect(() => { load() }, [])

  async function priceCheck(row) {
    setBusy(row.bookingId)
    setCheck(c => ({ ...c, [row.bookingId]: { loading: true } }))
    try {
      const { data } = await tournamentApi.post(`/admin/hotel-authorizations/${row.bookingId}/price-check`)
      setCheck(c => ({ ...c, [row.bookingId]: { loading: false, ...data } }))
      setConfirmUp(c => ({ ...c, [row.bookingId]: false }))
    } catch (e) {
      setCheck(c => ({ ...c, [row.bookingId]: { loading: false, error: e?.response?.data?.message || e.message } }))
    } finally { setBusy(null) }
  }

  async function diagnose(row) {
    setBusy(row.bookingId)
    setDiag(d => ({ ...d, [row.bookingId]: { loading: true } }))
    try {
      const { data } = await tournamentApi.post(`/admin/hotel-authorizations/${row.bookingId}/diagnose`)
      setDiag(d => ({ ...d, [row.bookingId]: { loading: false, hotels: data.hotels || [] } }))
    } catch (e) {
      setDiag(d => ({ ...d, [row.bookingId]: { loading: false, error: e?.response?.data?.message || e.message } }))
    } finally { setBusy(null) }
  }

  async function downloadJson(row) {
    setBusy(row.bookingId)
    try {
      const { data } = await tournamentApi.post(`/admin/hotel-authorizations/${row.bookingId}/export`)
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `hotel-auth-${row.quoteNumber || row.bookingId}.json`
      document.body.appendChild(a); a.click(); a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (e) { alert(e?.response?.data?.message || e.message) }
    finally { setBusy(null) }
  }

  async function authorize(row) {
    const chk = check[row.bookingId]
    const priceUp = chk && chk.anyPriceUp
    if (priceUp && !confirmUp[row.bookingId]) { alert('The price increased since the quote. Tick “Confirm the new price” to proceed.'); return }
    const upSeqs = selectedUpgrades(row)
    const deltaSum = selectedDeltaSum(row)
    let msg = `Authorize and BOOK the hotel(s) for booking #${row.bookingId} on the live provider now?\n\nThe customer money is already collected. This charges the corporate card.`
    if (upSeqs.length) {
      msg += `\n\nUpgrading ${upSeqs.length} hotel(s) to REFUNDABLE. The ${money(deltaSum, row.currency)} difference comes out of the professional fee (the reseller's margin), not the customer — and the quote flips to Refundable.`
    }
    if (!window.confirm(msg)) return
    setBusy(row.bookingId)
    try {
      const body = { confirmNewPrice: !!confirmUp[row.bookingId] }
      if (upSeqs.length) body.upgradeSequences = upSeqs
      const { data } = await tournamentApi.post(`/admin/hotel-authorizations/${row.bookingId}/authorize`, body)
      if (data.ok) {
        const upCount = (data.results || []).filter(r => r.upgraded).length
        setDone(d => ({ ...d, [row.bookingId]: `Booked — trip ${data.status}${upCount ? ` · ${upCount} upgraded to refundable` : ''}` }))
        setBookings(bs => bs.filter(b => b.bookingId !== row.bookingId))
      } else {
        // Surface the actual per-hotel failure reason (provider rejection etc.) —
        // the backend returns it per result; don't swallow it behind a generic notice.
        const fails = (data.results || []).filter(r => !r.ok)
        const detail = fails.map(r => `#${r.sequence}: ${r.message || 'failed'}`).join(' · ')
        setDone(d => ({ ...d, [row.bookingId]: `Some hotels failed — ${data.status}.${detail ? ' ' + detail : ''} Re-check and retry (or Download JSON for the full provider error).` }))
      }
    } catch (e) {
      const data = e?.response?.data
      if (data && data.code === 'price_changed') {
        setCheck(c => ({ ...c, [row.bookingId]: { loading: false, hotels: data.hotels, anyPriceUp: true } }))
        alert('The hotel price increased since the quote. Review the new price below, then tick “Confirm the new price” and authorize again.')
      } else if (data && (data.code === 'not_bookable' || data.code === 'upgrade_unavailable')) {
        setCheck(c => ({ ...c, [row.bookingId]: { loading: false, hotels: data.hotels, anyUnbookable: data.code === 'not_bookable' } }))
        alert(data.message || (data.code === 'upgrade_unavailable' ? 'The refundable upgrade is no longer available — re-check the price.' : 'A hotel is no longer bookable.'))
      } else if (data && data.code === 'no_current_price') {
        setCheck(c => ({ ...c, [row.bookingId]: { loading: false, hotels: data.hotels, anyUnbookable: true } }))
        alert(data.message || 'Could not read the current price — re-check before authorizing.')
      } else {
        alert((data && data.message) || e.message)
      }
    } finally { setBusy(null) }
  }

  const total = (bookings || []).length

  // What the corporate card is going to be charged for the hotels on this page,
  // grouped by currency. Prefer a live-checked net (after "Check price") over the
  // quoted net; a hotel whose net isn't known yet is counted as "not priced", NOT
  // as $0, so the total is never silently understated.
  const chargeTotals = useMemo(() => {
    const byCur = {}
    for (const row of (bookings || [])) {
      const cur = row.currency || 'CAD'
      const slot = byCur[cur] || (byCur[cur] = { known: 0, unpriced: 0 })
      const chk = check[row.bookingId]
      const list = (chk && chk.hotels) ? chk.hotels : (row.hotels || [])
      for (const h of list) {
        const net = (chk && chk.hotels)
          ? (h.currentNet != null ? h.currentNet : h.quotedNet)
          : h.quotedNet
        if (net == null) slot.unpriced += 1
        else slot.known = r2(slot.known + Number(net))
      }
    }
    return byCur
  }, [bookings, check])

  return (
    <AdminShell title="Hotel Authorizations">
      <div style={{ fontSize: 13.5, color: '#475569', lineHeight: 1.5, marginBottom: 14, maxWidth: 780 }}>
        Custom trips whose hotel is <strong>awaiting authorization</strong>. The customer's money is
        already collected; the hotel is <strong>not</strong> booked on the corporate card until you
        authorize it here — do that once the funds have settled and the card has room.
        Use <strong>Check price</strong> to re-quote live (quoted vs current cost) before you
        <strong> Authorize &amp; book</strong>. If the price rose since the quote you must confirm the new price.
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        {bookings && <div style={{ fontSize: 12.5, color: '#64748b' }}>{total} awaiting authorization</div>}
        <button type="button" onClick={load} style={{ border: '1px solid #e2e8f0', background: '#fff', color: '#475569', borderRadius: 8, fontSize: 13, fontWeight: 600, padding: '7px 12px', cursor: 'pointer' }}>Refresh</button>
      </div>

      {bookings && total > 0 && (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 14, padding: '10px 14px', background: '#f8fafc', border: '1px solid #eef2f7', borderRadius: 10 }}>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: '#475569' }}>Total hotel charge:</span>
          {Object.entries(chargeTotals).map(([cur, v], i) => (
            <span key={cur} style={{ fontSize: 13.5, color: '#0f172a' }}>
              {i > 0 && <span style={{ color: '#cbd5e1', marginRight: 10 }}>·</span>}
              <strong>{v.known > 0 ? money(v.known, cur) : '—'}</strong>
              <span style={{ fontSize: 11.5, color: '#94a3b8', fontWeight: 500 }}> {cur}</span>
              {v.unpriced > 0 && (
                <span style={{ fontSize: 11.5, color: '#9a3412', fontWeight: 600 }}> · {v.unpriced} not priced yet — run Check price</span>
              )}
            </span>
          ))}
        </div>
      )}

      {err && <div style={{ padding: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#991b1b', fontSize: 13 }}>{err}</div>}

      {bookings == null ? (
        <div style={{ padding: 14, color: '#94a3b8', fontSize: 13 }}>Loading…</div>
      ) : total === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', border: '1px dashed #cbd5e1', borderRadius: 10, color: '#94a3b8', fontSize: 13.5 }}>
          No hotels are awaiting authorization.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {bookings.map(row => {
            const isOpen = !!open[row.bookingId]
            const chk = check[row.bookingId]
            const near = row.daysToTrip != null && row.daysToTrip <= 14
            const covered = row.collectedTotal != null && row.quotedNetTotal != null && row.collectedTotal >= row.quotedNetTotal
            return (
              <div key={row.bookingId} style={{ border: '1px solid #e2e8f0', borderRadius: 10, background: '#fff' }}>
                <div onClick={() => setOpen(o => ({ ...o, [row.bookingId]: !o[row.bookingId] }))}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', cursor: 'pointer' }}>
                  <span style={{ color: '#94a3b8', fontSize: 12, width: 12 }}>{isOpen ? '▾' : '▸'}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a' }}>
                      {row.tripName || row.organizerName || `Booking #${row.bookingId}`}
                      <span style={{ fontFamily: 'monospace', fontWeight: 500, color: '#94a3b8', marginLeft: 8, fontSize: 12 }}>{row.quoteNumber || `#${row.bookingId}`}</span>
                    </div>
                    <div style={{ fontSize: 11.5, color: '#94a3b8' }}>
                      {row.resellerName || '—'} · {row.organizerName || '—'}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <div style={{ fontSize: 12.5, color: '#475569' }}>Hotel net <strong style={{ color: '#0f172a' }}>{money(row.quotedNetTotal, row.currency)}</strong></div>
                    <div style={{ fontSize: 11.5, color: covered ? '#16a34a' : '#9a3412' }}>Collected {money(row.collectedTotal, row.currency)}</div>
                  </div>
                  <div style={{ textAlign: 'right', whiteSpace: 'nowrap', minWidth: 96 }}>
                    <div style={{ fontSize: 12, color: '#475569' }}>{fmtDay(row.tripStartDate)}</div>
                    {row.daysToTrip != null && (
                      <div style={{ fontSize: 11.5, fontWeight: 700, color: near ? '#b91c1c' : '#64748b' }}>
                        {row.daysToTrip < 0 ? 'past' : `in ${row.daysToTrip}d`}
                      </div>
                    )}
                  </div>
                </div>

                {isOpen && (
                  <div style={{ borderTop: '1px solid #f1f5f9', padding: '12px 14px 14px 38px' }}>
                    {done[row.bookingId] && (
                      <div style={{ fontSize: 12.5, color: '#166534', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '8px 10px', marginBottom: 10 }}>{done[row.bookingId]}</div>
                    )}
                    {(chk && chk.hotels ? chk.hotels : row.hotels).map((h, i) => {
                      const live = chk && chk.hotels && chk.hotels[i]
                      const wb = live && live.willBook
                      // Prefer the quoted detail from the list row (it carries room type etc.).
                      const q = (row.hotels && row.hotels[i]) || h
                      return (
                        <div key={i} style={{ padding: '6px 0', borderBottom: '1px dashed #f1f5f9' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, fontSize: 12.5 }}>
                            <span style={{ color: '#0f172a', fontWeight: 600 }}>{h.name || q.name || `Hotel ${i + 1}`}{(h.source || q.source) && <span style={{ fontWeight: 500, color: '#94a3b8' }}> · {h.source || q.source}</span>}</span>
                            <span style={{ whiteSpace: 'nowrap', color: '#475569' }}>
                              Quoted {money(h.quotedNet != null ? h.quotedNet : q.quotedNet, row.currency)}
                              {live && live.currentNet != null && (
                                <span style={{ marginLeft: 8, color: live.priceUp ? '#b91c1c' : '#16a34a', fontWeight: 700 }}>
                                  → {money(live.currentNet, row.currency)}{live.delta != null && live.delta !== 0 ? ` (${live.delta > 0 ? '+' : ''}${money(live.delta, row.currency)})` : ''}
                                </span>
                              )}
                              {live && live.bookable === false && <span style={{ marginLeft: 8, color: '#b91c1c', fontWeight: 700 }}>not bookable{live.reason ? ` — ${reasonText(live.reason)}` : ''}</span>}
                            </span>
                          </div>
                          {/* What the customer was QUOTED (so staff can verify the booking matches). */}
                          <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 3 }}>
                            <strong style={{ color: '#475569' }}>As quoted:</strong> {q.roomType || '—'} · {q.rooms || '?'} room{q.rooms === 1 ? '' : 's'}{q.nights ? ` × ${q.nights} night${q.nights === 1 ? '' : 's'}` : ''} · {fmtDay(q.checkIn)} → {fmtDay(q.checkOut)}{row.numGolfers ? ` · ${row.numGolfers} golfers` : ''}{q.refundable != null ? ` · ${q.refundable ? 'refundable' : 'non-refundable'}` : ''}{q.quotedTotal != null ? ` · sell ${money(q.quotedTotal, row.currency)}` : ''}
                          </div>
                          {/* What the live pick will actually book (after Check price). */}
                          {wb && (
                            <div style={{ fontSize: 11.5, color: '#166534', marginTop: 2 }}>
                              <strong>Will book:</strong> {wb.provider || h.source} · {wb.roomType || '—'} · {wb.rooms || q.rooms} room{(wb.rooms || q.rooms) === 1 ? '' : 's'}{wb.adultsPerRoom ? ` · ${wb.adultsPerRoom} adults/room` : ''}{wb.refundable != null ? ` · ${wb.refundable ? 'refundable' : 'non-refundable'}` : ''}{wb.meal ? ` · ${wb.meal}` : ''} · net {money(wb.net, row.currency)}
                            </div>
                          )}
                          {/* Upgrade-to-refundable offer: a same-room refundable rate exists for a
                              hotel the customer was quoted NON-refundable. Ticking it books the
                              refundable rate and absorbs the delta from the professional fee. */}
                          {live && live.refundableAlt && live.upgradeDelta != null && live.quotedRefundable === false && (
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 5, fontSize: 11.5, color: '#0f766e', fontWeight: 600, cursor: 'pointer' }}>
                              <input type="checkbox" checked={!!(upgrade[row.bookingId] || {})[live.sequence]} onChange={() => toggleUpgrade(row.bookingId, live.sequence)} />
                              Upgrade to refundable ({live.upgradeDelta >= 0 ? '+' : ''}{money(live.upgradeDelta, row.currency)})
                              <span style={{ color: '#94a3b8', fontWeight: 500 }}>→ refundable net {money(live.refundableAlt.net, row.currency)} · from the professional fee</span>
                            </label>
                          )}
                        </div>
                      )
                    })}

                    {/* Upgrade vs professional-fee warning (sum of ticked upgrades). */}
                    {selectedUpgrades(row).length > 0 && chk && chk.serviceFee != null && (
                      selectedDeltaSum(row) > chk.serviceFee ? (
                        <div style={{ marginTop: 10, fontSize: 12, color: '#9a3412', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: '8px 10px' }}>
                          The refundable upgrade ({money(selectedDeltaSum(row), row.currency)}) exceeds the professional fee ({money(chk.serviceFee, row.currency)}) — the extra comes out of the rest of the reseller's margin. (Still allowed.)
                        </div>
                      ) : (
                        <div style={{ marginTop: 10, fontSize: 11.5, color: '#64748b' }}>
                          Upgrade {money(selectedDeltaSum(row), row.currency)} absorbed from the professional fee ({money(chk.serviceFee, row.currency)}).
                        </div>
                      )
                    )}

                    {chk && chk.anyPriceUp && (
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 12.5, color: '#9a3412', fontWeight: 600 }}>
                        <input type="checkbox" checked={!!confirmUp[row.bookingId]} onChange={e => setConfirmUp(c => ({ ...c, [row.bookingId]: e.target.checked }))} />
                        Confirm the new price (it rose since the quote)
                      </label>
                    )}

                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                      <button type="button" onClick={() => priceCheck(row)} disabled={busy === row.bookingId}
                        style={{ border: '1px solid #e2e8f0', background: '#fff', color: '#475569', borderRadius: 6, fontSize: 12.5, fontWeight: 700, padding: '6px 12px', cursor: busy === row.bookingId ? 'default' : 'pointer' }}>
                        {chk && chk.loading ? 'Checking…' : 'Check price'}
                      </button>
                      <button type="button" onClick={() => authorize(row)} disabled={busy === row.bookingId || (chk && chk.anyUnbookable)}
                        style={{ border: '1px solid #0f766e', background: '#0f766e', color: '#fff', borderRadius: 6, fontSize: 12.5, fontWeight: 700, padding: '6px 12px', cursor: (busy === row.bookingId || (chk && chk.anyUnbookable)) ? 'default' : 'pointer', opacity: (busy === row.bookingId || (chk && chk.anyUnbookable)) ? 0.6 : 1 }}>
                        {busy === row.bookingId ? 'Working…' : 'Authorize & book'}
                      </button>
                      <button type="button" onClick={() => diagnose(row)} disabled={busy === row.bookingId}
                        title="Read-only: why is this hotel bookable or not (books nothing)"
                        style={{ border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', borderRadius: 6, fontSize: 12.5, fontWeight: 700, padding: '6px 12px', cursor: busy === row.bookingId ? 'default' : 'pointer', marginLeft: 'auto' }}>
                        {diag[row.bookingId] && diag[row.bookingId].loading ? 'Diagnosing…' : 'Diagnose'}
                      </button>
                      <button type="button" onClick={() => downloadJson(row)} disabled={busy === row.bookingId}
                        title="Download a JSON with everything needed to book this hotel (stored inputs + live provider diagnostic) for troubleshooting"
                        style={{ border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', borderRadius: 6, fontSize: 12.5, fontWeight: 700, padding: '6px 12px', cursor: busy === row.bookingId ? 'default' : 'pointer' }}>
                        Download JSON
                      </button>
                    </div>

                    {diag[row.bookingId] && !diag[row.bookingId].loading && (
                      <div style={{ marginTop: 12, border: '1px solid #eef2f7', borderRadius: 8, background: '#f8fafc', padding: '10px 12px', fontSize: 12 }}>
                        {diag[row.bookingId].error ? (
                          <div style={{ color: '#b91c1c' }}>{diag[row.bookingId].error}</div>
                        ) : (
                          (diag[row.bookingId].hotels || []).map((d, di) => (
                            <div key={di} style={{ marginBottom: di < (diag[row.bookingId].hotels.length - 1) ? 12 : 0 }}>
                              <div style={{ fontWeight: 700, color: '#0f172a' }}>
                                {d.name || `Hotel ${di + 1}`}
                                {d.verdict && (
                                  <span style={{ marginLeft: 8, fontWeight: 700, color: d.verdict.bookable ? '#16a34a' : '#b91c1c' }}>
                                    {d.verdict.bookable ? 'bookable' : `not bookable — ${reasonText(d.verdict.reason)}`}
                                  </span>
                                )}
                              </div>
                              {d.error ? <div style={{ color: '#b91c1c' }}>{d.error}</div> : (
                                <>
                                  {d.stored && (
                                    <div style={{ color: '#475569', marginTop: 4 }}>
                                      Quoted: <strong>{d.stored.roomType || '—'}</strong> · refundable {yn(d.stored.refundable)} · net {money(d.stored.netCost, row.currency)} · target/room {money(d.match && d.match.targetPerRoom, row.currency)} · id {d.stored.externalId || '—'} ({d.stored.source || '—'})
                                    </div>
                                  )}
                                  {d.geo && (
                                    <div style={{ color: '#475569', marginTop: 4 }}>
                                      Area search: {d.geo.skipped ? 'skipped' : d.geo.error ? `error (${errMsg(d.geo.error)})` : `${d.geo.hotelsReturned} hotels · found ${yn(d.geo.found)} · ${d.geo.rateCount} rates · matched ${yn(d.geo.picked)}`}
                                    </div>
                                  )}
                                  {(d.byId || []).map((b, bi) => (
                                    <div key={bi} style={{ color: '#475569', marginTop: 4 }}>
                                      By-id via <strong>{b.provider}</strong>: {b.skipped ? 'skipped' : b.error ? `error (${errMsg(b.error)})` : `found ${yn(b.found)} · ${b.rateCount} rates · matched ${yn(b.picked)}`}
                                      {(b.sampleRates || []).length > 0 && (
                                        <div style={{ marginLeft: 12, color: '#94a3b8' }}>
                                          {b.sampleRates.map((s, si) => (
                                            <div key={si}>• {s.roomType || '—'} · refundable {yn(s.refundable)} · net {money(s.net, row.currency)}{s.total != null ? ` · total ${money(s.total, row.currency)}` : ''} · bookable {yn(s.bookable)}</div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </AdminShell>
  )
}
